"use client"

import { Link } from "./Link";
import { HeaderQueryResult } from "@/sanity.types";
import { headerTabStyle } from "@/lib/resolveHeaderTabColorScheme";
import { resolveHeaderNavLink } from "@/lib/resolveHeaderNavLink";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Plus from "./Plus";
import NavWaitlistButton from "./NavWaitlistButton";

type featuredLinkType = NonNullable<NonNullable<HeaderQueryResult>["header"]>["featuredLink"]
type linksType = NonNullable<NonNullable<HeaderQueryResult>["header"]>["links"]

export default function MobileMenu({featuredLink, links, blogLinkIndex = -1, showBlogLink = true} : {featuredLink: featuredLinkType, links: linksType, blogLinkIndex?: number, showBlogLink?: boolean}) {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();
  const shouldReduceMotion = useReducedMotion();
  const blogIsActive =
    pathname === '/blog' ||
    pathname.startsWith('/blog/') ||
    pathname === '/youth-sports' ||
    pathname.startsWith('/youth-sports/');
  const blogAriaCurrent =
    pathname === '/blog' ? 'page' : blogIsActive ? 'location' : undefined;
  const handleClick = () => {
    setMenuIsOpen(!menuIsOpen);
  }

  useEffect(() => {
    if (!menuIsOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuIsOpen(false);
        window.requestAnimationFrame(() => menuButtonRef.current?.focus());
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [menuIsOpen]);

  return (
    <div className="relative z-10 h-full min-w-0 shrink-0 pr-5 max-[360px]:pr-2 flex items-end">
      <button
        ref={menuButtonRef}
        onClick={handleClick}
        aria-label={menuIsOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuIsOpen}
        aria-controls="mobile-navigation"
        className="relative z-1 flex min-w-[4.4375rem] items-center justify-center body--small leading-none text-box-trim-both py-3 px-4 max-[360px]:px-3 rounded-t-[10px] cursor-pointer transition-colors bg-lilac text-royal-dark"
      >
        {menuIsOpen ? (
          <span aria-hidden="true" className="block size-4 rotate-45">
            <Plus />
          </span>
        ) : (
          'Menu'
        )}
      </button>

      <NavWaitlistButton
        label="Join Waitlist"
        className="relative z-0 block body--small leading-none text-box-trim-both py-3 px-4 max-[360px]:px-3 rounded-t-[10px] cursor-pointer whitespace-nowrap -ml-1.5 bg-(--color-background) text-(--color-foreground)"
        style={headerTabStyle('join waitlist', 'sun')}
      />

      <AnimatePresence>
        {menuIsOpen &&
          <motion.nav
            id="mobile-navigation"
            aria-label="Primary"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
            className="fixed inset-x-0 top-[3.375rem] w-screen overflow-hidden border-b border-soft-black bg-beige"
          >
            <div className="p-5">
              {links && links.map((headerLink, i) =>
                headerLink?.link &&
                  <Link
                    key={i}
                    link={resolveHeaderNavLink(headerLink.link)}
                    aria-current={i === blogLinkIndex ? blogAriaCurrent : undefined}
                    onClick={() => setMenuIsOpen(false)}
                    className={i === blogLinkIndex && blogIsActive ? 'underline underline-offset-4' : undefined}
                  >
                      <span
                        className="block body--large py-[0.5rem] text-center"
                      >
                        {headerLink.mobileText? headerLink.mobileText : headerLink.link.text}
                      </span>
                  </Link>
              )}
              {showBlogLink && (
                <NextLink
                  href="/blog"
                  aria-current={blogAriaCurrent}
                  onClick={() => setMenuIsOpen(false)}
                  className={blogIsActive ? 'underline underline-offset-4' : undefined}
                >
                  <span className="block body--large py-[0.5rem] text-center">
                    Blog
                  </span>
                </NextLink>
              )}
            </div>
          </motion.nav>
        }
      </AnimatePresence>
    </div>
  )
}
